import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { getSearchBoxGAData, getSearchBoxTrackingObj } from "../../utils/trackingHelper.js";
import { SearchBoxContext } from "../SearchBoxContext/index.js";
import { ANYWHERE } from "../../utils/constants.js";
const getForwardedExpParams = () => {
    if (typeof window === "undefined" || !window.location)
        return "";
    const expParams = new URLSearchParams();
    new URLSearchParams(window.location.search).forEach((value, key) => {
        if (key.startsWith("exp_"))
            expParams.set(key, value);
    });
    const expQuery = expParams.toString();
    return expQuery ? `&${expQuery}` : "";
};
const useAutoComplete = (props) => {
    const abortController = useRef();
    const timer = useRef();
    const [q, setQ] = useState("");
    const { segmentIndex, type } = props;
    const { autoCompleteBaseURL, getOptionalHeaders, i18n, lang = "en", searchType, segments, setSegmentFrom, setSegmentTo, trackV2, trackLandingGoal, onTrackSBExperimentCustomGoal, onTrackSBExperimentStage, disableAutocompleteMultiSelect, multiSelectVariant, multiSelectName, isMobile, enabledCountriesAndAnywhere, isSearchAnyCityEnabled, onGetSBExperimentVariant, recentSearchHistory, onRecentSearchSelect } = useContext(SearchBoxContext);
    const effectiveEnabledCountriesAndAnywhere = enabledCountriesAndAnywhere;
    const recentSearchHistoryEnabled = type === "to" && onGetSBExperimentVariant("flights_web_cat_searchbox_recent_search_history", 2) > 0;
    const effectiveRecentSearchHistory = recentSearchHistoryEnabled ? recentSearchHistory : undefined;
    const effectiveOnRecentSearchSelect = recentSearchHistoryEnabled ? onRecentSearchSelect : undefined;
    const isSingleFromSegment = segments?.[0]?.from.length <= 1;
    const isMultiStop = searchType === "MULTISTOP";
    const disableCountries = isMultiStop || !effectiveEnabledCountriesAndAnywhere || !isSingleFromSegment;
    const originCode = type === "to" ? segments?.[segmentIndex]?.from?.[0]?.code : undefined;
    const originType = type === "to" ? segments?.[segmentIndex]?.from?.[0]?.type : undefined;
    const provider = typeof window !== "undefined" && window.location && window.location.search.indexOf("ac=1") >= 0 ? "ours" : "brick";
    const originParam = originCode && originType ? `&origin=${originCode}&originType=${originType}` : "";
    const searchAnyCityParam = isSearchAnyCityEnabled ? "&searchAnyCity=true" : "";
    const expParam = getForwardedExpParams();
    const urlPath = `/api/autocomplete/${lang}?q=@@&type=${type}&disableCountries=${disableCountries}${originParam}${searchAnyCityParam}${expParam}`;
    const urlPathETG = `/api/autocomplete-etg/${lang}?q=@@`;
    const setSegment = useCallback((locations) => {
        const trackingObj = getSearchBoxTrackingObj(q, locations, segmentIndex);
        const gaData = getSearchBoxGAData(locations);
        if (type === "from") {
            if (trackLandingGoal && locations.length) {
                onTrackSBExperimentCustomGoal({
                    name: "recp_flightstech_webfunnel_flightssearch_recpipeline_web",
                    customGoal: 1
                });
            }
            trackV2("select_origin", trackingObj, gaData);
            setSegmentFrom(locations, segmentIndex);
        }
        else {
            trackV2("select_destination", trackingObj, gaData);
            setSegmentTo(locations, segmentIndex);
        }
    }, [type, segmentIndex, setSegmentFrom, setSegmentTo, q, trackV2, trackLandingGoal, onTrackSBExperimentCustomGoal]);
    const debounce = useCallback((func) => {
        return (...args) => {
            const later = () => {
                if (timer.current)
                    clearTimeout(timer.current);
                func(...args);
            };
            if (timer.current)
                clearTimeout(timer.current);
            timer.current = setTimeout(later, 400);
        };
    }, []);
    const initialState = useMemo(() => {
        return { loading: false, error: false, success: false };
    }, []);
    const [result, setResult] = useState([]);
    const [state, setState] = useState(initialState);
    const url = useMemo(() => {
        if (autoCompleteBaseURL) {
            return provider === "brick"
                ? `${autoCompleteBaseURL.url}${urlPath}&accessToken=${autoCompleteBaseURL.accessToken}`
                : `${autoCompleteBaseURL.url}${urlPathETG}&accessToken=${autoCompleteBaseURL.accessToken}`;
        }
        else {
            return provider === "brick" ? `${urlPath}` : `${urlPathETG}`;
        }
    }, [autoCompleteBaseURL, provider, urlPath, urlPathETG]);
    const search = useCallback(async (q) => {
        if (q.length < 2)
            return;
        setQ(q);
        setState({ ...initialState, loading: true });
        trackV2(type === "from" ? "type_origin" : "type_destination ", q);
        try {
            const searchString = encodeURIComponent(q.trim());
            if (abortController.current)
                abortController.current.abort();
            abortController.current = new AbortController();
            const optionalHeaders = typeof getOptionalHeaders === "function" ? getOptionalHeaders() : undefined;
            const headers = new Headers();
            headers.set("Accept", "application/json");
            if (optionalHeaders && typeof optionalHeaders === "object" && !Array.isArray(optionalHeaders)) {
                try {
                    Object.entries(optionalHeaders).forEach(([key, value]) => headers.set(key, value));
                }
                catch (error) {
                    console.error(error);
                    setState({ ...initialState, error: true });
                    return;
                }
            }
            const results = await fetch(url.replace("@@", searchString), {
                credentials: "include",
                headers,
                signal: abortController.current?.signal
            })
                .then((r) => r.json())
                .then((r) => r.map((_r, idx) => {
                _r["autoCompleteResultIndex"] = idx;
                return _r;
            }));
            setResult(results);
            setState({ ...initialState, success: true });
        }
        catch (err) {
            setResult([]);
            const error = err.name !== "AbortError";
            if (error)
                setState({ ...initialState, error });
        }
    }, [url, setState, setResult, initialState, trackV2, type]);
    const selectedLocations = useMemo(() => {
        return segments?.[segmentIndex]?.[type] || [];
    }, [type, segmentIndex, segments]);
    const removeLocationByIndex = useCallback((idx) => {
        if (idx < 0)
            return;
        const result = [...selectedLocations];
        result.splice(idx, 1);
        setSegment(result);
    }, [setSegment, selectedLocations]);
    const addLocation = useCallback((loc) => {
        const result = selectedLocations.filter((s) => !((s.code === loc.code && s.type === loc.type) || (loc.type === "CITY" && s["parent"] === loc.code)));
        const newSegment = loc.type === ANYWHERE ? [loc] : [...result.filter((item) => item.type !== ANYWHERE), loc];
        setSegment(newSegment);
    }, [setSegment, selectedLocations]);
    const setLocation = useCallback((loc) => {
        setSegment([loc]);
    }, [setSegment]);
    const removeLocation = useCallback((loc) => {
        const result = selectedLocations.filter((s) => s.code !== loc.code);
        setSegment([...result]);
    }, [setSegment, selectedLocations]);
    return {
        addLocation,
        setLocation,
        i18n,
        isMultiStop,
        removeLocation,
        removeLocationByIndex,
        result,
        search: debounce(search),
        selectedLocations,
        state,
        trackV2,
        disableAutocompleteMultiSelect,
        multiSelectVariant,
        multiSelectName,
        onTrackSBExperimentCustomGoal,
        onTrackSBExperimentStage,
        onGetSBExperimentVariant,
        searchType,
        isMobile,
        recentSearchHistory: effectiveRecentSearchHistory,
        onRecentSearchSelect: effectiveOnRecentSearchSelect,
        rawRecentSearchHistory: recentSearchHistory
    };
};
export default useAutoComplete;
